import RequestHistory from '../components/agents/RequestHistory';
import RunOutputs from '../components/RunOutputs';
import OutputSelector from '../components/OutputSelector';
import { Button, Icon } from '../components/ui';
import { useUI } from '../context/UIContext';
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams, useLocation } from 'react-router-dom';
import { api, apiError } from '../api/client';
import { describeDownloadError, downloadFromResponse } from '../lib/download';
import { useAuth } from '../context/AuthContext';
import TokenQuotaSummary from '../components/TokenQuotaSummary';
import ResearchProgress from '../components/ResearchProgress';
import SessionActivity from '../components/SessionActivity';
import { sessionActivity } from '../lib/sessionActivity';
import { useSessionData } from '../hooks/useSessionData';
import {
  agentLabel, artifactLabel, flagKindLabel, flagSeverityColor, isAttemptLimit,
  skillKindLabel, skillStatusLabel, statusBadge,
} from '../lib/format';
import Transcript from '../components/chat/Transcript';
import Composer from '../components/chat/Composer';
import SessionAttachments from '../components/SessionAttachments';
import TrajectoryView from '../components/trajectory/TrajectoryView';
import ReportWorkspace from '../components/ReportWorkspace';
import { sessionStatus } from '../lib/sessionStatus';
import './SessionPage.css';

const NEAR_BOTTOM_THRESHOLD = 80; // px

const newClientMsgId = () =>
  (typeof crypto !== 'undefined' && crypto.randomUUID)
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(16).slice(2)}`;

export default function SessionPage() {
  const { confirm } = useUI();
  const { user } = useAuth();
  const { projectId, sessionId } = useParams();
  const focusOutputId = new URLSearchParams(useLocation().search).get('output');
  const {
    session, transcript, tasks, goals, artifacts, finalAnswer,
    error, loading, invalidate, refreshNow, syncedAt,
  } = useSessionData(sessionId);

  const [auxiliary, setAuxiliary] = useState(null);
  const [workVisited, setWorkVisited] = useState(false);
  const [selectedOutputId, setSelectedOutputId] = useState(null);
  const [outputSummary, setOutputSummary] = useState({});
  const auxiliaryRef = useRef(null);
  auxiliaryRef.current = auxiliary;
  const auxiliaryTitle = useRef(null);
  const previousControl = useRef(null);
  const pendingFocus = useRef(null);
  useLayoutEffect(() => {
    const target = pendingFocus.current;
    if (!target) return;
    pendingFocus.current = null;
    if (target === 'auxiliary') auxiliaryTitle.current?.focus();
    else if (target === 'answer') document.querySelector('.chat-composer textarea')?.focus();
    else if (previousControl.current?.isConnected && previousControl.current.getClientRects().length) previousControl.current.focus();
    else document.querySelector('.session-header-right > button')?.focus();
  }, [auxiliary]);
  const openPanel = useCallback(kind => {
    previousControl.current = document.activeElement;
    if (auxiliaryRef.current === kind) {
      pendingFocus.current = null;
      auxiliaryTitle.current?.focus();
    } else pendingFocus.current = 'auxiliary';
    setAuxiliary(kind);
    if (kind === 'work') setWorkVisited(true);
    if (kind === 'materials') setSelectedOutputId(null);
    document.querySelector('.session-header .actions-menu')?.removeAttribute('open');
  }, []);
  const closePanel = useCallback(() => {
    pendingFocus.current = 'return';
    setAuxiliary(null);
  }, []);
  const autoOpened = useRef(new Set());
  useEffect(() => { autoOpened.current.clear(); }, [sessionId]);
  useEffect(() => {
    let active = true;
    const load = async () => {
      try {
        const data = await api.sessionOutputs(sessionId);
        if (!active || window.innerWidth <= 1000) return;
        const item = data.items.find(value => value.source_first && value.run_id === data.current_run_id && ['queued', 'running'].includes(value.status));
        if (!item || autoOpened.current.has(data.current_run_id)) return;
        autoOpened.current.add(data.current_run_id);
        if (!auxiliaryRef.current) { setSelectedOutputId(item.document_id || item.id); setAuxiliary('output'); }
      } catch { /* Session polling displays connectivity failures. */ }
    };
    load(); const timer = setInterval(load, 1500);
    return () => { active = false; clearInterval(timer); };
  }, [sessionId]);
  const openOutput = useCallback((item, { fromLink = false } = {}) => {
    setSelectedOutputId(item?.document_id || item?.id || null);
    if (fromLink) setAuxiliary(item ? 'output' : 'materials');
    else openPanel(item ? 'output' : 'materials');
  }, [openPanel]);
  // Polled separately from useSessionData: the plan changes rarely (only as
  // skills complete), so it does not need that hook's 4s cadence.
  const [sessionSkills, setSessionSkills] = useState([]);
  // Карта возможностей приходит с сервера — она выводится из реестра
  // рендереров, и появление нового формата не должно требовать релиза фронта.
  const [exportFormats, setExportFormats] = useState(null);
  // Ключ вида `${id}:${fmt}`, а не общий флаг: иначе клик по одному артефакту
  // гасит кнопки у всех остальных.
  const [busyExport, setBusyExport] = useState(null);
  const [exportError, setExportError] = useState(null);
  const [exportingLogs, setExportingLogs] = useState(false);
  const [openDocuments, setOpenDocuments] = useState([]);
  const [activeDocumentId, setActiveDocumentId] = useState(null);
  const [latestReport, setLatestReport] = useState(null);
  useEffect(() => {
    setAuxiliary(null); setWorkVisited(false); setSelectedOutputId(null);
    setOutputSummary({}); setOpenDocuments([]); setActiveDocumentId(null); setLatestReport(null);
  }, [sessionId]);
  useEffect(() => {
    if (focusOutputId) setAuxiliary('materials');
  }, [sessionId, focusOutputId]);
  const [continuationOutputs, setContinuationOutputs] = useState(null);
  const [continuationLoaded, setContinuationLoaded] = useState(false);
  const lastOutputRun = useRef(null);
  const rememberOutputSelection = useCallback((value, runId) => {
    if (lastOutputRun.current !== runId) { setContinuationOutputs(value?.length ? value : null); lastOutputRun.current = runId; }
    setContinuationLoaded(true);
  }, []);
  useEffect(() => { setContinuationOutputs(null); setContinuationLoaded(false); lastOutputRun.current = null; }, [sessionId]);
  const moderationRevision = session?.moderation?.revision || 0;
  const openDocument = useCallback(resource => {
    const next = { ...resource, revision: resource.revision ?? null };
    setOpenDocuments(current => current.some(d => d.id === next.id)
      ? current.map(d => d.id === next.id ? { ...d, ...next } : d) : [...current, next]);
    setActiveDocumentId(resource.id);
    openPanel('document');
  }, [openPanel]);
  const openTranscriptDocument = useCallback(async entry => {
    try {
      if (entry.output_id) { openOutput({ id: entry.output_id, document_id: entry.document_id }); return; }
      if (entry.kind === 'final_report') {
        const report = entry.report_document_id ? await api.getReportDocument(entry.report_document_id) : await api.getLatestReportDocument(sessionId);
        if (report?.outline?.output_id) {
          openOutput({ id: report.outline.output_id });
        } else if (report) openDocument({ id: report.id, type: 'report', title: report.title,
          revision: entry.is_current_version === false ? entry.document_revision : null });
      } else openDocument({ id: entry.source_id || entry.id, type: 'artifact', title: entry.title });
    } catch (e) { setActionError(apiError(e)); }
  }, [openDocument, openOutput, sessionId]);
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const report = await api.getLatestReportDocument(sessionId);
        if (cancelled) return;
        setLatestReport(report);
      } catch { /* regular session polling still reports connectivity errors */ }
    };
    load();
    const timer = setInterval(load, 2500);
    return () => { cancelled = true; clearInterval(timer); };
  }, [sessionId]);

  useEffect(() => {
    if (!latestReport || latestReport.outline?.output_id
      || !['preparing', 'generating', 'reviewing'].includes(latestReport.status)
      || latestReport.session_id !== sessionId
      || (session?.active_run_id && latestReport.run_id !== session.active_run_id)
      || window.innerWidth <= 1000) return;
    const key = `report:${latestReport.id}`;
    if (autoOpened.current.has(key)) return;
    autoOpened.current.add(key);
    if (!auxiliaryRef.current) openDocument({ id: latestReport.id, type: 'report', title: latestReport.title });
  }, [latestReport, session?.active_run_id, sessionId, openDocument]);


  // Refetched whenever an artifact appears: a skill turning `done` is exactly
  // what produces one, so that is the cheapest reliable trigger.
  useEffect(() => {
    let cancelled = false;
    api.getExportFormats()
      .then(data => { if (!cancelled) setExportFormats(data); })
      .catch(() => { if (!cancelled) setExportFormats({}); });
    return () => { cancelled = true; };
  }, []);

  // Флаги наблюдателя. Отдельным опросом и по тому же поводу, что план:
  // монитор ходит раз в несколько минут, ему не нужна четырёхсекундная
  // частота основного хука. Отсутствие флагов — нормальный ответ, поэтому
  // ошибка загрузки гасится: панель вспомогательная, страницу она не ломает.
  const [flags, setFlags] = useState([]);
  const [actionError, setActionError] = useState(null);
  const [actionBusy, setActionBusy] = useState(false);
  const reloadFlags = useCallback(() => {
    api.listSessionFlags(sessionId)
      .then(setFlags)
      .catch(() => { /* лента наблюдателя вспомогательная */ });
  }, [sessionId]);

  useEffect(() => { reloadFlags(); const timer = setInterval(reloadFlags, 15000); return () => clearInterval(timer); }, [reloadFlags]);

  const acknowledgeFlag = async (flagId) => {
    try {
      await api.acknowledgeSessionFlag(sessionId, flagId);
    } finally {
      // Перечитываем в любом случае: подтверждение могло пройти, а ответ —
      // не дойти, и тогда локальное состояние разошлось бы с сервером.
      reloadFlags();
    }
  };

  useEffect(() => {
    let cancelled = false;
    api.listSessionSkills(sessionId)
      .then(data => { if (!cancelled) setSessionSkills(data); })
      .catch(() => { /* план — вспомогательная панель, её отсутствие не ломает страницу */ });
    return () => { cancelled = true; };
    // session?.version (bumped on every orchestrator decision) is in the
    // deps, not just artifacts.length: a methodology can be attached or
    // dropped by a decision that produces no new artifact at all — without
    // this the plan would only refresh once something else happened to
    // trigger it.
  }, [sessionId, artifacts.length, session?.version]);
  const [finalizing, setFinalizing] = useState(false);
  const [exporting, setExporting] = useState(false);

  // Optimistic sends, keyed by an id minted before the request. The server
  // echoes it back on the durable entry, so reconciliation is by identity —
  // no duplicate, no gap, no text matching.
  const [pendingSends, setPendingSends] = useState([]);
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState(null);
  const sendErrorOwner = useRef(null);
  const submissions = useRef(new Map());
  useEffect(() => { submissions.current.clear(); }, [sessionId]);

  const messagesRef = useRef(null);
  const messagesEndRef = useRef(null);
  const lastEntryIdRef = useRef(null);

  const isNearBottom = useCallback(() => {
    const el = messagesRef.current;
    if (!el) return true;
    return el.scrollHeight - el.scrollTop - el.clientHeight < NEAR_BOTTOM_THRESHOLD;
  }, []);

  const entries = transcript?.entries ?? [];
  const activity = useMemo(() => sessionActivity(tasks, goals, artifacts), [tasks, goals, artifacts]);

  const submit = useCallback(async (text, attachmentIds = [], onConfirmed) => {
    const target = transcript?.composer_target_id;
    const kind = transcript?.composer_mode === 'answer' ? 'user_answer' : 'user_message';
    const key = JSON.stringify([sessionId, kind, target, text, [...attachmentIds].sort()]);
    const existing = submissions.current.get(key);
    if (existing) {
      existing.onConfirmed = onConfirmed;
      return existing.send();
    }
    const clientMsgId = newClientMsgId();
    const submission = { clientMsgId, onConfirmed };
    const confirm = () => {
      if (submission.confirmed) return;
      submission.confirmed = true;
      if (sendErrorOwner.current === clientMsgId) {
        sendErrorOwner.current = null;
        setSendError(null);
      }
      submission.onConfirmed?.();
      submissions.current.delete(key);
      setPendingSends(p => p.filter(x => x.client_msg_id !== clientMsgId));
    };
    submission.confirm = confirm;

    const send = async () => {
      setSending(true);
      sendErrorOwner.current = null;
      setSendError(null);
      setPendingSends(p => p.map(x => (
        x.client_msg_id === clientMsgId ? { ...x, localStatus: 'sending' } : x
      )));
      invalidate();               // any in-flight poll is stale from here on
      try {
        if (kind === 'user_answer') {
          await api.answerHumanRequest(target, text, clientMsgId, attachmentIds);
        } else {
          await api.sendSessionMessage(sessionId, text, clientMsgId, attachmentIds);
        }
        confirm();
        await refreshNow().catch(() => {});
      } catch (e) {
        if (submission.confirmed) return;
        setPendingSends(p => p.map(x => (
          x.client_msg_id === clientMsgId ? { ...x, localStatus: 'failed' } : x
        )));
        sendErrorOwner.current = clientMsgId;
        setSendError(e);
        throw e;
      } finally {
        setSending(false);
      }
    };
    submission.send = send;
    submissions.current.set(key, submission);

    setPendingSends(p => [...p, {
      id: `local:${clientMsgId}`,
      client_msg_id: clientMsgId,
      kind,
      human_request_id: kind === 'user_answer' ? target : undefined,
      role: 'user',
      text,
      ts: new Date().toISOString(),
      localStatus: 'sending',
      onRetry: () => { send().catch(() => {}); },
    }]);

    await send();
  }, [invalidate, refreshNow, sessionId, transcript]);

  useEffect(() => {
    const confirmed = new Set(entries.map(entry => entry.client_msg_id).filter(Boolean));
    for (const submission of submissions.current.values()) {
      if (confirmed.has(submission.clientMsgId)) submission.confirm();
    }
  }, [entries]);

  // A confirmed server entry retires its optimistic twin. Two independent
  // layers: the POST response removes it, and this catches the case where a
  // poll beat the POST response back.
  const rows = useMemo(() => {
    const confirmed = new Set(entries.map(e => e.client_msg_id).filter(Boolean));
    return [...entries, ...pendingSends.filter(p => !confirmed.has(p.client_msg_id))];
  }, [entries, pendingSends]);

  useEffect(() => {
    if (rows.length === 0) return;
    const lastId = rows[rows.length - 1].id;
    if (lastId === lastEntryIdRef.current) return;
    const first = lastEntryIdRef.current === null;
    lastEntryIdRef.current = lastId;
    if (first || isNearBottom()) {
      requestAnimationFrame(() => {
        messagesEndRef.current?.scrollIntoView(first ? undefined : { behavior: 'smooth' });
      });
    }
  }, [rows, isNearBottom]);

  const handleExportDocx = async () => {
    setExporting(true);
    setExportError(null);
    try {
      const response = await api.exportFinalAnswerDocx(sessionId);
      downloadFromResponse(response, `${finalAnswer?.title || 'report'}.docx`);
    } catch (e) {
      setExportError(await describeDownloadError(e));
    } finally {
      setExporting(false);
    }
  };

  const handleExportArtifact = async (artifact, fmt) => {
    const key = `${artifact.id}:${fmt}`;
    setBusyExport(key);
    setExportError(null);
    try {
      const response = await api.exportArtifact(artifact.id, fmt);
      downloadFromResponse(response, `${artifactLabel(artifact)}.${fmt}`);
    } catch (e) {
      setExportError(await describeDownloadError(e));
    } finally {
      setBusyExport(null);
    }
  };

  // Карточка в чате несёт не артефакт, а его проекцию (source_id + тип +
  // слаг методики), поэтому экспорт идёт через ту же ручку, что и в правой
  // панели, но с собранным по ней объектом — иначе имя файла потеряло бы
  // методику и все выгрузки назывались бы «skill_document».
  const handleExportTranscriptCard = (entry, fmt) => handleExportArtifact(
    {
      id: entry.source_id,
      artifact_type: entry.artifact_type,
      skill_slug: entry.kind_label,
    },
    fmt,
  );

  const handleExportLogs = async () => {
    setExportingLogs(true);
    setExportError(null);
    try {
      const response = await api.exportSessionObservability(sessionId);
      downloadFromResponse(response, `observability-${sessionId}.zip`);
    } catch (e) {
      setExportError(await describeDownloadError(e));
    } finally {
      setExportingLogs(false);
    }
  };

  const handleFinalize = async () => {
    if (!await confirm('Агенты завершат исследование и сформируют результат по уже собранным данным.', { title: 'Получить результат досрочно?', action: 'Сформировать результат', variant: 'primary' })) return;
    setFinalizing(true);
    invalidate();
    try {
      await api.finalizeSession(sessionId);
      await refreshNow();
    } catch (err) {
      setActionError(apiError(err));
      await refreshNow();
    } finally {
      setFinalizing(false);
    }
  };

  const runAction = async (fn) => {
    if (actionBusy) return;
    setActionBusy(true);
    setActionError(null);
    invalidate();
    try { await fn(); await refreshNow(); }
    catch (err) { setActionError(apiError(err)); await refreshNow(); }
    finally { setActionBusy(false); }
  };

  if (loading && !session) return <div className="loading">Загрузка...</div>;
  if (!session) {
    return (
      <div className="alert alert-error">
        {error ? `Не удалось загрузить сессию: ${error.message}` : 'Сессия не найдена'}
      </div>
    );
  }

  const isActive = ['running', 'paused', 'waiting_for_human'].includes(session.status);
  const pendingIds = transcript?.pending_question_ids ?? [];
  const pendingQuestion = entries.find(
    e => e.kind === 'agent_question' && e.human_request_id === transcript?.composer_target_id
  );

  const progress = pendingQuestion ? { text: 'Нужен ваш ответ', attention: true } : sessionStatus(session, tasks, outputSummary);
  const materialArtifacts = artifacts.filter(a => !['user_prompt', 'human_answer'].includes(a.artifact_type));
  const readyMaterialCount = (outputSummary.availableCount || outputSummary.readyCount || 0)
    + (latestReport && ['ready', 'edited'].includes(latestReport.status) && !latestReport.outline?.output_id ? 1 : 0);
  const hasCurrentReport = latestReport && (!session.active_run_id || latestReport.run_id === session.active_run_id)
    && (['ready', 'edited'].includes(latestReport.status)
      || rows.some(entry => entry.kind === 'final_report' && entry.report_document_id === latestReport.id));
  const hasFinalResult = session.status === 'finished' && (hasCurrentReport || outputSummary.currentRunAvailableCount > 0 || outputSummary.currentRunReadyCount > 0
    || (finalAnswer && (!session.active_run_id || finalAnswer.run_id === session.active_run_id))
    || (!session.active_run_id && rows.some(entry => entry.kind === 'final_report')));
  const outputWarning = outputSummary.error ? 'Не удалось обновить файлы'
    : outputSummary.failed ? 'Нужен повтор'
    : outputSummary.needsReview ? 'Требуется проверка' : '';
  const titles = { materials: 'Материалы', output: 'Документ', document: 'Документ', work: 'Ход работы', settings: 'Настройки' };

  return (
    <div className="session-root session-calm">
      {actionError && <div role="alert" className="alert alert-error">{actionError}</div>}
      {exportError && <div role="alert" className="alert alert-error">{exportError}</div>}
      {session.moderation?.restricted && <div role="status" className="alert alert-error">Работа ограничена администратором. {session.moderation.reasons.join(' ')} Доступна разрешённая история.</div>}
      <div className="session-header">
        <div className="session-header-left">
          <Link to={`/projects/${projectId}`} className="text-muted back-link">← Проект</Link>
          <h1 className="session-title" title={session.title}>{session.title}</h1>
        </div>
        <div className="session-header-right">
          <Button onClick={() => openPanel('materials')} aria-expanded={auxiliary === 'materials' || auxiliary === 'output'}>
            Материалы{readyMaterialCount > 0 && <span className="badge" aria-label={`Материалов с содержанием: ${readyMaterialCount}`}>{readyMaterialCount}</span>}
            {outputWarning && <span className="session-material-warning">{outputWarning}</span>}
          </Button>
          {session.status === 'running' && <Button disabled={actionBusy || session.moderation?.restricted}
            onClick={() => runAction(() => api.pauseSession(sessionId))}>Пауза</Button>}
          {['paused', 'failed'].includes(session.status) && <Button variant="primary" disabled={actionBusy || session.moderation?.restricted}
            onClick={() => runAction(() => api.resumeSession(sessionId))}>{session.status === 'failed' ? 'Возобновить' : 'Продолжить'}</Button>}
          {isActive && <Button variant="primary" onClick={handleFinalize} disabled={finalizing || session.moderation?.restricted}>
            {finalizing ? 'Финализация...' : 'Получить результат'}</Button>}
          <details className="actions-menu"><summary className="icon-button" aria-label="Ещё" title="Ещё"><Icon name="more" /></summary>
            <div className="actions-menu-panel">
              <Button onClick={() => openPanel('settings')}>Настройки сессии</Button>
              <Button onClick={() => openPanel('work')}>Ход работы</Button>
              {isActive && <Button variant="danger" disabled={actionBusy || session.moderation?.restricted} onClick={async () => {
                if (await confirm('Текущая работа будет остановлена. Уже полученные результаты сохранятся.', { title: 'Отменить сессию?', action: 'Отменить сессию' })) runAction(() => api.cancelSession(sessionId));
              }}>Отменить сессию</Button>}
              {user?.is_admin && <Button onClick={handleExportLogs} disabled={exportingLogs}>
                {exportingLogs ? 'Собираем архив…' : 'Скачать логи сессии'}</Button>}
            </div>
          </details>
        </div>
        <div className="session-header-progress">
          <button type="button" className={`session-status ${progress.attention || error ? 'needs-attention' : ''}`}
            aria-label={`Ход работы: ${progress.text}`} aria-expanded={auxiliary === 'work'} onClick={() => openPanel('work')}>
            <span className="session-status-dot" aria-hidden="true" />
            <span role="status" title={progress.text}>{progress.objective
              ? <><span className="session-current-agent">В работе: {progress.agent} — </span>{progress.objective}</>
              : progress.text}</span><span className="session-status-link" aria-hidden="true">Подробнее ›</span>
          </button>
          {!hasFinalResult && <SessionActivity compact activity={activity} session={session} error={error} />}
        </div>
      </div>
      <div className="session-body" data-aux-open={auxiliary ? 'true' : 'false'}>
        <div className="chat-area">
          {error && <div role="alert" className="chat-stale-banner">
            Обновление не удалось ({error.message}). Показаны последние загруженные данные.
            <Button onClick={refreshNow}>Повторить</Button>
          </div>}
          <div className="chat-messages" ref={messagesRef}>
            <Transcript rows={rows} onOpenDocument={openTranscriptDocument} onExportDocx={handleExportDocx}
              completed={hasFinalResult}
              exporting={exporting} composerTargetId={transcript?.composer_target_id} exportFormats={exportFormats}
              onExportArtifact={handleExportTranscriptCard} busyExport={busyExport} />
            {!hasFinalResult && <>
              <ResearchProgress session={session} checklist />
              <SessionActivity inline activity={activity} session={session} error={error} syncedAt={syncedAt} />
            </>}
            <div ref={messagesEndRef} />
          </div>
          <Composer key={sessionId} projectId={projectId} mode={transcript?.composer_mode ?? 'disabled'}
            question={pendingQuestion} extraPendingCount={Math.max(0, pendingIds.length - 1)}
            onSubmit={submit} sending={sending} error={sendError} />
        </div>
        <aside className="session-auxiliary" hidden={!auxiliary} aria-labelledby="session-auxiliary-title"
          onKeyDown={event => {
            if (event.key === 'Escape' && !event.defaultPrevented && !event.target.closest('dialog[open]')) {
              event.preventDefault(); closePanel();
            }
          }}>
          <div className="session-auxiliary-header">
            <h2 id="session-auxiliary-title" ref={auxiliaryTitle} tabIndex={-1}>{titles[auxiliary] || 'Материалы'}</h2>
            <Button onClick={closePanel}>К диалогу</Button>
          </div>
          {pendingQuestion && <Button className="session-answer-link" onClick={() => {
            pendingFocus.current = 'answer';
            setAuxiliary(null);
          }}>Нужен ваш ответ → К вопросу</Button>}
          <section className="session-auxiliary-content session-materials" hidden={!['materials', 'output'].includes(auxiliary)} aria-label="Материалы сессии">
            <RunOutputs key={sessionId} sessionId={sessionId} compact readOnly={session.moderation?.restricted}
              selectedOutputId={selectedOutputId} onOpen={openOutput} onSummary={setOutputSummary}
              focusOutputId={focusOutputId} onSelection={rememberOutputSelection} />
            <div hidden={auxiliary === 'output'}>
              {latestReport && <button type="button" className="session-material-row" onClick={() => openTranscriptDocument({ kind: 'final_report', report_document_id: latestReport.id })}>
                <span>{latestReport.title}</span><span className="text-muted">Открыть отчёт →</span>
              </button>}
              <SessionAttachments key={`${sessionId}:${moderationRevision}`} sessionId={sessionId} onChange={refreshNow}
                disabled={session.moderation?.restricted || ['created', 'cancelled'].includes(session.status)} />
              <details className="session-material-group"><summary>Промежуточные материалы · {materialArtifacts.length}</summary>
                {materialArtifacts.length === 0 && <p className="text-muted">Материалы появятся по ходу исследования.</p>}
                {materialArtifacts.map(art => <button type="button" key={art.id} className="session-material-row"
                  onClick={() => openDocument({ id: art.id, type: 'artifact', title: art.title })}>
                  <span>{art.title}</span><span className="text-muted">{artifactLabel(art)} · v{art.version} →</span>
                </button>)}
              </details>
            </div>
          </section>
          <section className="session-document-content" hidden={auxiliary !== 'document'}>
            {openDocuments.length > 0 && <ReportWorkspace embedded readOnly={session.moderation?.restricted}
              documents={openDocuments} activeId={activeDocumentId} sessionId={sessionId} onOpen={openDocument}
              onActivate={setActiveDocumentId} onClose={id => {
                setOpenDocuments(docs => docs.filter(d => d.id !== id));
                setActiveDocumentId(current => current === id ? null : current);
                if (openDocuments.length === 1) setAuxiliary(current => current === 'document' ? 'materials' : current);
              }} />}
          </section>
          <section className="session-auxiliary-content" hidden={auxiliary !== 'work'} aria-label="Ход работы сессии">
            <SessionActivity activity={activity} session={session} error={error} syncedAt={syncedAt} />
            <ResearchProgress session={session} />
            <details className="session-work-section"><summary>Задачи · {tasks.length}</summary>
{                tasks.length === 0
                  ? <div className="empty">Задач пока нет</div>
                  : tasks.map(task => (
                    <div key={task.id} className="sidebar-item">
                      <div className="flex gap-2 mb-1" style={{ flexWrap: 'wrap' }}>
                        {statusBadge(task.status === 'failed' && isAttemptLimit(task.last_error) ? 'stopped' : task.status)}
                        <span className="badge" style={{ background: 'var(--bg-secondary)', color: 'var(--text)' }}>{agentLabel(task.assigned_to)}</span>
                      </div>
                      <div className="sidebar-item-text">{task.objective}</div>
                      {task.status !== 'done' && task.last_error && <div style={{ color: isAttemptLimit(task.last_error) ? 'var(--text-muted)' : 'var(--danger)', fontSize: '0.78rem', marginTop: 4 }}>{task.last_error}</div>}
                      {task.status === 'failed' && (
                        <Button className="btn btn-secondary btn-sm mt-2"
                          disabled={actionBusy || session.moderation?.restricted} onClick={() => runAction(() => api.retryTask(task.id))}>Повтор</Button>
                      )}
                    </div>
                  ))}
            </details>
            <details className="session-work-section"><summary>Цели · {goals.length}</summary>
{                goals.length === 0
                  ? <div className="empty">Цели появятся при запуске задач</div>
                  : goals.map(goal => (
                    <div key={goal.id} className="sidebar-item">
                      <div className="flex gap-2 mb-1" style={{ flexWrap: 'wrap' }}>
                        <span className="badge" style={{ background: goal.status === 'accepted' ? 'var(--success-bg)' : isAttemptLimit(goal.stop_reason) ? 'var(--bg-secondary)' : ['blocked', 'budget_exhausted'].includes(goal.status) ? 'var(--danger-bg)' : 'var(--primary-light)', color: 'var(--text)' }}>
                          {goal.status === 'accepted' ? 'принята' : goal.status === 'budget_exhausted' ? 'лимит исчерпан' : goal.status === 'blocked' ? 'остановлена' : goal.status === 'review' ? 'проверка' : 'в работе'}
                        </span>
                        <span className="badge" style={{ background: 'var(--bg-secondary)', color: 'var(--text)' }}>{agentLabel(goal.assigned_to)}</span>
                        <span className="text-muted" style={{ fontSize: '0.78rem' }}>попытка {goal.attempt_count}/{goal.max_attempts}</span>
                      </div>
                      <div className="sidebar-item-text">{goal.objective}</div>
                      <ul style={{ margin: '8px 0', paddingLeft: 18, fontSize: '0.82rem' }}>
                        {goal.criteria.map(c => <li key={c.id}>{c.description}</li>)}
                      </ul>
                      {goal.reviews?.length > 0 && (() => {
                        const review = goal.reviews[goal.reviews.length - 1];
                        return <div style={{ color: review.accepted ? 'var(--success)' : 'var(--warning)', fontSize: '0.78rem', marginTop: 4 }}>
                          {review.accepted ? 'Приёмка: выполнены все критерии' : `Замечания: ${review.rationale}`}
                        </div>;
                      })()}
                      {goal.stop_reason && <div style={{ color: isAttemptLimit(goal.stop_reason) ? 'var(--text-muted)' : 'var(--danger)', fontSize: '0.78rem', marginTop: 4 }}>{goal.stop_reason}</div>}
                    </div>
                  ))}
            </details>
            <details className="session-work-section"><summary>Траектория</summary>
              {workVisited && <TrajectoryView key={`${sessionId}:${moderationRevision}`} sessionId={sessionId} />}
            </details>
            <details className="session-work-section"><summary>История вопросов</summary>
              {(() => {
                const questions = entries.filter(e => e.kind === 'agent_question');
                const answers = new Map(
                  entries.filter(e => e.kind === 'user_answer' && e.human_request_id)
                    .map(e => [e.human_request_id, e.text])
                );
                return questions.length === 0
                  ? <div className="empty">Запросов нет</div>
                  : questions.map(q => (
                    <div key={q.id} className="sidebar-item">
                      <div className="flex gap-2 mb-1">{statusBadge(q.question_status)}</div>
                      <div className="sidebar-item-text">{q.text}</div>
                      {answers.has(q.human_request_id) && (
                        <div className="text-muted mt-2" style={{ fontSize: '0.8rem' }}>
                          <strong>Ответ:</strong> {answers.get(q.human_request_id)}
                        </div>
                      )}
                    </div>
                  ));
              })()}
            </details>
            <details className="session-work-section"><summary>Диагностика</summary>
              <p className="text-muted">Итерация {session.current_iteration} · Цикл {session.current_cycle}</p>
              {flags.length > 0 && <details><summary>Наблюдатель · {flags.filter(f => !f.acknowledged_at).length}</summary>
{                flags.length === 0
                  ? <div className="empty">Наблюдатель ничего не отметил</div>
                  : <>
                    <div className="text-muted" style={{ fontSize: '0.78rem', marginBottom: 8 }}>
                      Это сигнал, а не остановка: платформа ни на что из этого не
                      реагирует и работу не прерывает. Смотрит человек.
                    </div>
                    {flags.map(flag => (
                      <div key={flag.id} className="sidebar-item"
                        style={{ opacity: flag.acknowledged_at ? 0.55 : 1 }}>
                        <div className="flex gap-2 mb-1" style={{ flexWrap: 'wrap' }}>
                          <span className="badge" style={{
                            background: 'var(--bg-secondary)', color: flagSeverityColor(flag.severity),
                          }}>
                            {flagKindLabel(flag.kind)}
                          </span>
                          {flag.acknowledged_at && (
                            <span className="badge" style={{ background: 'var(--bg-secondary)', color: 'var(--text)' }}>
                              просмотрено
                            </span>
                          )}
                        </div>
                        <div className="sidebar-item-text">{flag.summary}</div>
                        {flag.evidence && (
                          // Доказательство показывается целиком и рядом с
                          // флагом: флаг без него — мнение, а мнение стоит
                          // человеку того же взгляда, что и его отсутствие.
                          <pre style={{
                            fontSize: '0.72rem', whiteSpace: 'pre-wrap',
                            background: 'var(--bg-secondary)', padding: 6, borderRadius: 4,
                            marginTop: 6, maxHeight: 160, overflow: 'auto',
                          }}>{flag.evidence}</pre>
                        )}
                        {!flag.acknowledged_at && (
                          <Button className="btn btn-secondary btn-sm mt-2"
                            onClick={() => acknowledgeFlag(flag.id)}>
                            Посмотрел
                          </Button>
                        )}
                      </div>
                    ))}
                  </>}
              </details>}
              {user?.is_admin && <>
                <RequestHistory key={sessionId} sessionId={sessionId} />
                <details><summary>Результаты скиллов · {sessionSkills.length}</summary>
{                sessionSkills.length === 0
                  ? <div className="empty">Результаты скиллов не запланированы</div>
                  : sessionSkills.map(item => {
                    // Legacy rows from before the orchestrator chose skills
                    // itself have no params.source at all — read as manually
                    // attached, which is what they in fact were.
                    const source = item.params?.source;
                    const sourceLabel = source === 'auto' ? 'выбрано оркестратором'
                      : source === 'system' ? 'платформа'
                      : 'выбрано вручную';
                    const rationale = item.params?.rationale || item.params?.drop_reason;
                    return (
                    <div key={item.id} className="sidebar-item">
                      <div className="flex gap-2 mb-1" style={{ flexWrap: 'wrap' }}>
                        {statusBadge(item.status === 'done' ? 'done' : item.status)}
                        <span className="badge" style={{ background: 'var(--bg-secondary)', color: 'var(--text)' }}>
                          {skillKindLabel(item.kind)}
                        </span>
                        <span className="badge" style={{ background: 'var(--primary-light)', color: 'var(--primary)' }}>
                          {sourceLabel}
                        </span>
                        <span className="text-muted" style={{ fontSize: '0.78rem' }}>
                          {agentLabel(item.department)}
                        </span>
                      </div>
                      <div className="sidebar-item-text">{item.title}</div>
                      <div className="text-muted" style={{ fontSize: '0.78rem', marginTop: 2 }}>
                        {item.slug} · {skillStatusLabel(item.status)}
                        {item.attempts > 0 && ` · попыток: ${item.attempts}`}
                      </div>
                      {rationale && (
                        <div className="text-muted" style={{ fontSize: '0.78rem', marginTop: 2, fontStyle: 'italic' }}>
                          {rationale}
                        </div>
                      )}
                    </div>
                    );
                  })}
                </details>
              </>}
            </details>
          </section>
          <section className="session-auxiliary-content" hidden={auxiliary !== 'settings'} aria-label="Настройки сессии">
            <label className="form-group"><span className="form-label">Уровень проверки</span>
              <select className="form-control" aria-label="Уровень проверки" value={session.review_level || 'balanced'}
                onChange={e => runAction(() => api.setReviewLevel(sessionId, e.target.value))} disabled={actionBusy || session.moderation?.restricted}>
                <option value="soft">Мягкая</option><option value="balanced">Сбалансированная</option><option value="strict">Строгая</option>
              </select>
            </label>
            <TokenQuotaSummary paused={session.pause_reason === 'token_quota_exceeded'} />
            {session.status === 'finished' && continuationLoaded && <details><summary>Результаты следующего исследования</summary>
              <OutputSelector projectId={projectId} value={continuationOutputs} onChange={setContinuationOutputs} />
            </details>}
            <ResearchProgress session={session} busy={actionBusy || session.moderation?.restricted || (session.status === 'finished' && (!continuationLoaded || !continuationOutputs?.length))}
              onContinue={depth => runAction(() => api.reopenSession(sessionId, null, depth, continuationOutputs))} />
          </section>
        </aside>
      </div>
    </div>
  );
}
